// This hook loads the shared cart module and gives the app simple helpers to use it.

// Use React tools we need for the cart helpers.
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

// Reuse the shared script loader so the cart follows the same external module pattern.
import { useExternalScript } from "./useExternalScript";

// Reuse the existing auth token and fulfilment/order APIs for post-checkout processing.
import { AuthContext } from "global/context/AuthContext";

// Import the Strich API calls for fulfilment and order management.
import {
    getFulfilmentServicesBy,
    getOrder,
    getOrderByUID,
    requestFulfilmentCost,
    sendForFulfilment,
} from "api/APIStrichCalls";

// Read the portal account details from user context.
import { UserContext } from "global/context/UserContext";

// Read the portal theme settings from theme context.
import { ThemeContext } from "global/context/ThemeContext";

// Import the stored app config getter. 
import { GetAppConfig } from "global/config/AppConfigStore";

// Reuse one cache-busting token for the whole page load so the cart script is refreshed once without loading multiple copies.
const CART_CACHE_BUST = Date.now();

// Match the portal theme primary colour options so cart settings can reuse the same resolved colour value.
const PRIMARY_COLOUR_MAP = {
    purple: "#6a0dad",
    blue: "#007bff",
    pink: "#ff1493",
    orange: "#ff7f00",
    green: "#28a745",
};

// Match the portal light and dark theme tokens so cart settings can inherit the same overall look and feel.
const THEME_COLOUR_MAP = {
    light: {
        style_primary_content_colour: "#ffffff",
        style_secondary_colour: "#d9d9d9",
        style_secondary_content_colour: "#1e1e2f",
        style_base_100_colour: "#faf7f5",
        style_base_100_content_colour: "#1e1e2f",
        style_base_200_colour: "#ffffff",
        style_base_200_content_colour: "#1e1e2f",
        style_base_300_colour: "#eeeeee",
        style_base_300_content_colour: "#1e1e2f",
    },
    dark: {
        style_primary_content_colour: "#ffffff",
        style_secondary_colour: "#12121e",
        style_secondary_content_colour: "#ffffff",
        style_base_100_colour: "#1e1e2f",
        style_base_100_content_colour: "#ffffff",
        style_base_200_colour: "#2e3147",
        style_base_200_content_colour: "#ffffff",
        style_base_300_colour: "#1e2137",
        style_base_300_content_colour: "#ffffff",
    },
};

// Keep track of which checkout orders have already been sent for fulfilment so we do not send the same order twice.
function getSharedProcessedCheckoutOrders() {

    // Ensure the shared Strich namespace exists on window.
    window.strich = window.strich || {};

    // Ensure the shared cart namespace exists on window.
    window.strich.cart = window.strich.cart || {};

    // Ensure the shared processed checkout orders set exists on window.
    if (!(window.strich.cart.__portalProcessedCheckoutOrders instanceof Set)) {

        // Assign the shared set on the cart namespace
        window.strich.cart.__portalProcessedCheckoutOrders = new Set();

    }

    // Return the shared set of processed checkout orders.
    return window.strich.cart.__portalProcessedCheckoutOrders;

}

// Give the app one simple way to use the shared cart module.
export function useCart() {

    // Reuse the logged-in token for fulfilment follow-up after checkout completes.
    const { token } = useContext(AuthContext) || {};

    // Read the loaded runtime config only when this hook is called.
    const appConfig = GetAppConfig();

    // Read the portal account details when available.
    const { userData } = useContext(UserContext) || {};

    // Read the active portal theme when available so cart styles can track live theme changes.
    const { theme = "light", primary = "blue" } = useContext(ThemeContext) || {};

    // Get the cart script URL from the app config
    const cartUrl = appConfig?.cartUrl;

    // Reuse the saved portal theme choices so the cart module receives one consistent set of colours.
    const themeColours = useMemo(() => {
        // Resolve the primary colour from the portal theme so the cart module can use the same colour.
        const primaryColour = PRIMARY_COLOUR_MAP[String(primary || "blue").trim().toLowerCase()] || PRIMARY_COLOUR_MAP.blue;

        // Are we using a light or dark theme? 
        const baseThemeColours = THEME_COLOUR_MAP[String(theme || "light").trim().toLowerCase()] || THEME_COLOUR_MAP.light;

        // Return the full set of colours for the cart module to use.
        return {
            ...baseThemeColours,
            style_primary_colour: primaryColour,
            style_accent_colour: primaryColour,
        };
    }, [primary, theme]);

    // Add one stable cache buster so cart loads do not get stuck on an older browser-cached script.
    const cacheBustedCartUrl = useMemo(() => {

        // Stop when there is no configured script URL.
        if (!cartUrl) {
            return "";
        }

        // Reuse one stable cache-busting token for this page load.
        const separator = cartUrl.includes("?") ? "&" : "?";

        // Return the cart URL with a cache-busting query parameter.
        return `${cartUrl}${separator}v=${CART_CACHE_BUST}`;

    }, [cartUrl]);

    // Use the alias stored on the portal account details so cart operations stay tied to the current account.
    const defaultStoreAlias = userData?.portal_connection_alias || "";

    // Pass these cart settings directly into the cart module.
    // We're setting portal specific settings here aswell as passing through the theme colours so the cart module can match the portal theme.
    const cartSettings = useMemo(() => ([
        {
            code: "cart_basket_popup",
            value: "N",
        },
        {
            code: "style_primary_colour",
            value: themeColours.style_primary_colour,
        },
        {
            code: "style_primary_content_colour",
            value: themeColours.style_primary_content_colour,
        },
        {
            code: "style_secondary_colour",
            value: themeColours.style_secondary_colour,
        },
        {
            code: "style_secondary_content_colour",
            value: themeColours.style_secondary_content_colour,
        },
        {
            code: "style_accent_colour",
            value: themeColours.style_accent_colour,
        },
        {
            code: "style_accent_content_colour",
            value: themeColours.style_primary_content_colour,
        },
        {
            code: "style_base_100_colour",
            value: themeColours.style_base_100_colour,
        },
        {
            code: "style_base_100_content_colour",
            value: themeColours.style_base_100_content_colour,
        },
        {
            code: "style_base_200_colour",
            value: themeColours.style_base_200_colour,
        },
        {
            code: "style_base_200_content_colour",
            value: themeColours.style_base_200_content_colour,
        },
        {
            code: "style_base_300_colour",
            value: themeColours.style_base_300_colour,
        },
        {
            code: "style_base_300_content_colour",
            value: themeColours.style_base_300_content_colour,
        },
    ]), [themeColours]);

    // Load the shared cart script once and track its state.
    const status = useExternalScript(cacheBustedCartUrl);

    // Read the current cart API from the shared Strich namespace.
    const cart = window.strich?.cart || null;

    // Remember whether this hook instance has already auto-loaded the cart UI.
    const hasAutoLoadedCartRef = useRef(false);

    // Load the floating cart UI when the caller needs it.
    const loadCart = useCallback(async (
        storeAlias = defaultStoreAlias,
        uniquefields = ["sku", "colourway_code"],
        settings = cartSettings,
    ) => {

        // Stop here if the script is not ready yet.
        if (status !== "ready") {
            throw new Error("Cart script not ready");
        }

        // Stop here if the cart loader is missing on the page.
        if (typeof window.strich?.cart?.loadCart !== "function") {
            throw new Error("Cart not available on window");
        }

        // Let the shared cart module mount or refresh its own UI.
        await window.strich.cart.loadCart(storeAlias, uniquefields, settings);

        // Return the loaded cart API for convenience.
        return window.strich.cart;

    }, [cartSettings, defaultStoreAlias, status]);

    // Add an item to the current cart through the shared cart API.
    const addItem = useCallback((item, storeAlias = defaultStoreAlias) => {

        // Stop here if the script is not ready yet.
        if (status !== "ready") {
            throw new Error("Cart script not ready");
        }

        // Stop here if the cart addItem helper is missing on the page.
        if (typeof window.strich?.cart?.addItem !== "function") {
            throw new Error("Cart not available on window");
        }

        // Let the shared cart module own item normalization and storage.
        window.strich.cart.addItem(item, storeAlias);

        // Return the current cart API for convenience.
        return window.strich.cart;

    }, [defaultStoreAlias, status]);

    // After checkout succeeds, send the order for fulfilment and set the matching portal order status.
    const handleCheckoutSuccess = useCallback(async (checkoutDetail = {}) => {
        // Gets checkoutDetail from the strich:checkout-success event.

        // Read the order ID
        const orderId = checkoutDetail?.order_id;
        
        // Read the order UID   
        const orderUid = checkoutDetail?.order_uid;

        // Use the order ID if available, otherwise fallback to the order UID for tracking.
        const processedOrderKey = orderId || orderUid;

        // Keep track of the resolved order ID for fulfilment and status updates.
        let resolvedOrderId = orderId;

        // Stop early if we cannot authenticate or if checkout returned no usable order reference.
        if (!token || !processedOrderKey) {

            // Output a warning to the console if checkout succeeded but no order ID or UID was returned.
            if (!processedOrderKey) {

                // If cart.js doesn't return an order ID or UID, we cannot send the order for fulfilment. Send a warning to the console.
                console.warn("Checkout succeeded without an order id or order uid. Fulfilment was skipped.", checkoutDetail);
            }

            return;
        }

        // Get the shared set of already processed checkout orders
        const processedCheckoutOrders = getSharedProcessedCheckoutOrders();

        // Prevent duplicate fulfilment requests when the success callback fires more than once for the same order.
        if (processedCheckoutOrders.has(processedOrderKey)) {
            return;
        }

        // Mark this order as already processed so we do not send it for fulfilment again.
        processedCheckoutOrders.add(processedOrderKey);

        try {
            // Get the order details from the Strich API using either the order ID or order UID.
            const orderResponse = orderId ? await getOrder(token, orderId) : await getOrderByUID(token, orderUid);

            // Read Order details from the response
            const orderDetails = orderResponse?.results;

            // Resolve the order ID and delivery country for fulfilment.
            resolvedOrderId = String(orderDetails?.id || orderId || "").trim();

            // Read the delivery country from the order details.
            const deliveryCountry = String(orderDetails?.deliverycountry || "").trim();

            // Stop here if the order details or delivery country are missing, as we cannot send the order for fulfilment.
            if (!orderResponse?.success || !orderDetails || !resolvedOrderId || !deliveryCountry) {

                // Throw an error so the catch block can mark the order as pending for follow-up.
                throw new Error(orderResponse?.error_message || "Order details or delivery country were unavailable");
            
            }

            // Pick the first available fulfilment service for the destination before pricing and submitting the order.
            const servicesResponse = await getFulfilmentServicesBy(token, deliveryCountry);
            
            // Read the first service code from the response.
            const firstServiceCode = String(servicesResponse?.results?.[0]?.service_code || "").trim();

            // Stop here if no fulfilment service was available for the destination country.            
            if (!servicesResponse?.success || !firstServiceCode) {

                // Throw an error so the catch block can mark the order as pending for follow-up.
                throw new Error(servicesResponse?.error_message || "No fulfilment service was available");
            
            }

            // Build the payload for the fulfilment request.
            const fulfilmentPayload = {
                order_id: resolvedOrderId,
                service_code: firstServiceCode,
            };

            // Confirm fulfilment pricing succeeds before the order is actually submitted for fulfilment.
            const fulfilmentCostResponse = await requestFulfilmentCost(token, fulfilmentPayload);

            // Stop here if the fulfilment cost request failed, as we cannot send the order for fulfilment.
            if (!fulfilmentCostResponse?.success) {

                // Throw an error so the catch block can mark the order as pending for follow-up.
                throw new Error(fulfilmentCostResponse?.error_message || "Could not fetch fulfilment cost");
            
            }

            // Send the order for fulfilment through the Strich API.
            const fulfilmentResponse = await sendForFulfilment(token, fulfilmentPayload);

            // Fulfilment succeeded, so leave the API-owned order status unchanged and finish.
            if (fulfilmentResponse?.success) {
                return;
            }

            // If we are here, fulfilment failed and the API owns the resulting order status.
            throw new Error(fulfilmentResponse?.error_message || "Send for fulfilment failed");
    
        } catch (error) {

            // Do not change the order status here because the API owns all post-checkout status transitions.
            console.error(`Post-checkout fulfilment failed for order ${orderId || orderUid}.`, error);
        }
    
    }, [token]);

    // Keep the mounted cart UI in sync when the portal theme changes after the cart has already loaded.
    useEffect(() => {

        // Wait until the cart script is ready.
        if (status !== "ready") {
            return;
        }

        // Stop when the cart settings updater is not available yet.
        if (typeof window.strich?.cart?.updateSettings !== "function") {
            return;
        }

        // Push the latest style settings into the mounted cart module.
        window.strich.cart.updateSettings(cartSettings, {
            store_alias: defaultStoreAlias || "default",
        });

    }, [cartSettings, defaultStoreAlias, status]);

    // Register the checkout success bridge before cart auto-load runs so return handlers can trigger fulfilment immediately.
    useEffect(() => {

        // Wait until the cart script is ready.
        if (status !== "ready") {
            return;
        }

        // Stop when the checkout success handler is not available yet.
        const handleCheckoutSuccessEvent = (event) => {
            void handleCheckoutSuccess(event?.detail || {});
        };

        // Register the checkout success event listener on window so the cart module can trigger it after checkout completes.
        window.addEventListener("strich:checkout-success", handleCheckoutSuccessEvent);

        // Cleanup the event listener when this hook instance unmounts (so we don't get dupe events)
        return () => {
            window.removeEventListener("strich:checkout-success", handleCheckoutSuccessEvent);
        };

    }, [handleCheckoutSuccess, status]);

    // Mount the shared cart UI automatically once the script is ready.
    useEffect(() => {

        // Wait until the cart script is ready.
        if (status !== "ready") {
            return;
        }

        // Wait until the portal account details have provided the default store alias.
        if (!defaultStoreAlias) {
            return;
        }

        // Only auto-load once for this hook instance.
        if (hasAutoLoadedCartRef.current) {
            return;
        }

        // Mark that this hook instance has already auto-loaded the cart UI.
        hasAutoLoadedCartRef.current = true;

        // Let the shared cart module mount itself globally.
        void loadCart().catch((error) => {

            // Output any errors to the console for debugging, but do not throw so the app can continue to function.
            console.error("Failed to auto-load cart.", error);
            
        });

    }, [loadCart, status, defaultStoreAlias]);

    // Expose the shared cart helpers and simple ready state flags.
    return {

        // The shared cart API on window when it is available.
        cart,

        // Main way to mount the cart UI and checkout modal.
        loadCart,

        // Main way to add an item to the cart.
        addItem,

        // True when the script has finished loading.
        isReady: status === "ready",

        // True while the script is still loading.
        isLoading: status === "loading",

        // True when the script failed to load.
        error: status === "error",

        // Expose the raw status as well.
        status,

    };

}
